// Default ML invoker. Used by DmnSimulator unless the host overrides it via
// the `setMlInvoker(custom)` exported below. Hosts that proxy through their
// own backend (e.g. sfd-v2 hitting /api/v1/ml/invoke) replace this with a
// matching `{ invoke(payload) }` shape.
//
// Phase 1: REST is supported; PMML and ONNX throw `not_implemented` so the
// simulator records a clear error in trace+warnings instead of silently
// producing null. Real PMML/ONNX runtimes land in Phase 2.

export class MlInvocationError extends Error {
  constructor(code, msg) {
    super(`[simulator/ml] ${msg}`);
    this.code = code;
  }
}

let _customInvoker = null;

/**
 * Replace the default invoker with a host-provided implementation. Call
 * with `null` to revert to the default. The custom object must expose
 * `invoke({ runtime, endpoint, modelRef, modelVersion, features }) → Promise<{outputs, confidence}>`.
 */
export function setMlInvoker(invoker) {
  _customInvoker = invoker || null;
}

export function getMlInvoker() {
  return _customInvoker || defaultMlInvoker;
}

async function invokeRest({ endpoint, modelRef, modelVersion, features }) {
  if (!endpoint) {
    throw new MlInvocationError('missing_endpoint', 'runtime "rest" requires an endpoint');
  }
  if (typeof fetch !== 'function') {
    throw new MlInvocationError(
      'fetch_unavailable',
      'global fetch is unavailable — provide a custom mlInvoker via setMlInvoker(...)',
    );
  }
  let res;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ features, modelRef, modelVersion }),
    });
  } catch (err) {
    throw new MlInvocationError('network_error', `fetch failed: ${err.message}`);
  }
  if (!res.ok) {
    throw new MlInvocationError('http_error', `endpoint returned HTTP ${res.status}`);
  }
  let body;
  try {
    body = await res.json();
  } catch {
    throw new MlInvocationError('bad_response', 'endpoint did not return JSON');
  }
  if (!body || typeof body !== 'object' || !body.outputs) {
    throw new MlInvocationError('bad_response',
      'endpoint response missing required `outputs` field');
  }
  return {
    outputs: body.outputs,
    confidence: typeof body.confidence === 'number' ? body.confidence : null,
  };
}

export const defaultMlInvoker = {
  async invoke(payload) {
    const runtime = payload && payload.runtime;
    if (runtime === 'rest') return invokeRest(payload);
    if (runtime === 'pmml' || runtime === 'onnx') {
      throw new MlInvocationError(
        'not_implemented',
        `runtime '${runtime}' is not implemented in the default invoker (Phase 2). Use setMlInvoker(...) to provide a custom one.`,
      );
    }
    throw new MlInvocationError(
      'unknown_runtime',
      `unsupported runtime '${runtime || ''}' (expected rest|pmml|onnx)`,
    );
  },
};
